import { ReloadOutlined } from '@ant-design/icons';
import { Drawer, Flex, Select, Typography } from 'antd';
import Button from 'components/Button';
import { DefaultOptionType } from 'antd/es/select';
import { useEffect, useId, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import {
  selectToken,
  selectUserCountry,
  selectUserEmail,
} from 'store/auth/selectors';

import RBCountryFilter from 'components/UniversalFilters/RBCountryFilter';
import PageHeaderLayout from '../../system-utils/shared/components/PageHeaderLayout';
import SalesTable from './components/SalesTable';

import useAirtableConfig from '../shared/hooks/useAirtableConfig';
import useAllUserBasesQuery from './hooks/useAllUserBasesQuery';
import useBaseTableRecordsQuery from './hooks/useBaseTableRecordsQuery';
import useBaseTablesQuery from './hooks/useBaseTablesQuery';

import { ROLE_GROUPS } from 'utils/constants';
import { isAuthorizedToAccessResource } from 'utils/helpers';
import {
  urlFriendlyIdentifier,
  urlFriendlyJRAGIdentifier,
} from '../shared/config';
import { FieldMeta, FieldsConfig, FilterType } from '../shared/types';

import '../style.scss';

const PAGE_CONFIG = {
  header: 'Sales Deals',
  description: 'Manage your sales leads and opportunities.',
};

interface FilterSectionProps {
  baseOptions: DefaultOptionType[];
  tableOptions: DefaultOptionType[];
  selectedBase: string | null;
  selectedTable: string | null;
  onBaseChange: (value: string) => void;
  onTableChange: (value: string | null) => void;
  isBaseLoading: boolean;
  isTableLoading: boolean;
  tableFilters: Record<string, FilterType> | null;
  onTableFilterChange?: (key: string, value: string) => void;
}

const FilterSection = ({
  baseOptions,
  tableOptions,
  selectedBase,
  selectedTable,
  tableFilters,
  onTableFilterChange,
  onBaseChange,
  onTableChange,
  isBaseLoading,
  isTableLoading,
}: FilterSectionProps) => (
  <Flex gap={18} wrap="wrap">
    <Flex gap={3} vertical className="sales-table-filter">
      <Typography.Text type="secondary">Choose a Base:</Typography.Text>
      <Select
        disabled={isBaseLoading}
        loading={isBaseLoading}
        options={baseOptions}
        placeholder="Eg: Sales CRM"
        value={selectedBase}
        onChange={(value) => {
          onBaseChange(value);
          onTableChange(null);
        }}
      />
    </Flex>
    <Flex gap={3} vertical className="sales-table-filter">
      <Typography.Text type="secondary">
        Choose the table from the base
      </Typography.Text>
      <Select
        disabled={isTableLoading || !selectedBase}
        loading={isTableLoading}
        options={tableOptions}
        placeholder="Eg: Sales Reports"
        value={selectedTable}
        onChange={onTableChange}
      />
    </Flex>

    {tableFilters &&
      Object.entries(tableFilters)?.map(([key, value]) => (
        <Flex key={key} vertical gap={4} className="sales-table-filter">
          <Typography.Text type="secondary">{key}</Typography.Text>
          <Select
            allowClear
            placeholder={`Select ${key}`}
            style={{ width: '100%' }}
            options={value?.options}
            onChange={(val) => onTableFilterChange?.(key, val)}
          />
        </Flex>
      ))}
  </Flex>
);

interface ActionButtonsProps {
  onNewLead: () => void;
  onRefresh: () => void;
  isRefreshing: boolean;
}

const ActionButtons = ({
  onNewLead,
  onRefresh,
  isRefreshing,
}: ActionButtonsProps) => (
  <Flex gap={8}>
    <Button type="primary" onClick={onNewLead}>
      Add New Lead
    </Button>
    <Button
      trackerLabel="Refresh"
      loading={isRefreshing}
      icon={<ReloadOutlined />}
      onClick={onRefresh}
    />
  </Flex>
);

interface LeadFormDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  formUrl: string;
  jrAgRecordId?: string;
}

const LeadFormDrawer = ({
  isOpen,
  onClose,
  formUrl,
  jrAgRecordId,
}: LeadFormDrawerProps) => (
  <Drawer
    open={isOpen}
    onClose={onClose}
    footer={null}
    width={720}
    styles={{ body: { padding: 0, margin: 0 } }}
  >
    <iframe
      key={useId()}
      className="airtable-embed"
      src={`${formUrl}?prefill_Jr.%20Ag=${jrAgRecordId || ''}`}
      width="100%"
      height="100%"
      style={{ background: 'transparent' }}
      title="New Lead Form"
    />
  </Drawer>
);

const SalesPage: React.FC = () => {
  const [selectedBase, setSelectedBase] = useState<string | null>(null);
  const [selectedTable, setSelectedTable] = useState<string | null>(null);
  const [selectedCountry, setSelectedCountry] = useState<string | null>(
    useSelector(selectUserCountry),
  );

  const [isNewLeadFormOpen, setIsNewLeadFormOpen] = useState(false);

  const token = useSelector(selectToken);
  const userEmail = useSelector(selectUserEmail);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [offsetMap, setOffsetMap] = useState<Record<number, string>>({});

  const { menuOptions: userBasesMenuOptions, isLoading: isBaseLoading } =
    useAllUserBasesQuery(selectedCountry as string);

  const isUserRoleJrAg = useMemo(
    () => isAuthorizedToAccessResource(token, ROLE_GROUPS.JR_AGS),
    [token],
  );

  const urlFriendlyEmail = useMemo(
    () =>
      isUserRoleJrAg ? `'${encodeURIComponent(userEmail || '') || ''}'` : null,
    [userEmail],
  );

  const {
    data: baseTables,
    menuOptions: baseTableMenuOptions,
    isLoading: isTableLoading,
  } = useBaseTablesQuery(selectedBase, selectedCountry as string);

  const { getSelectedTableConfig } = useAirtableConfig(
    selectedCountry as string,
  );

  const selectedTableConfig = useMemo(() => {
    return getSelectedTableConfig(selectedBase!, selectedTable!);
  }, [selectedBase, selectedTable, getSelectedTableConfig]);

  const fieldsToRender = selectedTableConfig?.fieldsToRender as FieldsConfig;
  const userTableId = selectedTableConfig?.userTableId;

  const tableFilters = selectedTableConfig?.filters;

  const [appliedFilters, setAppliedFilters] = useState<Record<string, string>>(
    {},
  );

  const handleFilterChange = (key: string, value: string) => {
    setAppliedFilters({
      ...appliedFilters,
      [key]: value,
    });
  };

  const filterParams = useMemo(() => {
    let baseFormula = 'AND(';
    let filters = '';

    if (Object.keys(appliedFilters).length) {
      filters = Object.entries(appliedFilters)
        ?.filter(([_, value]) => !!value)
        .map(([key, value], i) => {
          return (
            (i > 0 ? ',' : '') +
            `${encodeURIComponent(`{${key}}`)}='${encodeURIComponent(value)}'`
          );
        })
        ?.join('');
    }

    baseFormula += filters + ')';

    return baseFormula;
  }, [
    urlFriendlyEmail,
    urlFriendlyIdentifier,
    selectedBase,
    selectedTable,
    appliedFilters,
  ]);

  const {
    data: tableRecordData,
    error: tableRecordsError,
    isLoading: isTableRecordsLoading,
    refetch: refetchTableRecords,
    isFetching: isTableRecordsFetching,
  } = useBaseTableRecordsQuery({
    baseId: selectedBase,
    tableId: selectedTable,
    filterByFormula: filterParams,
    offset: offsetMap?.[currentPage],
    pageSize,
  });

  const { records, offset } = tableRecordData || {};

  const { data: jrAgRecords } = useBaseTableRecordsQuery({
    baseId: selectedBase,
    tableId: userTableId,
    filterByFormula: urlFriendlyJRAGIdentifier + urlFriendlyEmail,
    pageSize: 1,
    enabled: isUserRoleJrAg,
  });

  const jrAgRecordId = useMemo(() => jrAgRecords?.[0]?.id, [jrAgRecords]);

  const selectedTableFieldMeta = useMemo(() => {
    const tbl = baseTables?.find(
      (table: Record<string, unknown>) => table?.id === selectedTable,
    );
    const tableFields = tbl?.fields;
    if (!tableFields) return null;

    return tableFields.reduce(
      (acc: Record<string, FieldMeta>, field: FieldMeta) => {
        const fieldData = {
          ...field,
          ...(field?.type === 'multipleRecordLinks' && {
            linkedTableId: field.options?.linkedTableId,
            args: [selectedBase, field.options?.linkedTableId],
          }),
        };

        return { ...acc, [field.name]: fieldData };
      },
      {},
    );
  }, [selectedBase, selectedTable, baseTables]);

  const handlePaginationChange = (page: number, size: number) => {
    if (size !== pageSize) {
      // Page size changed, reset pagination
      setPageSize(size);
      setCurrentPage(1);
      setTotalRecords(0);
      setOffsetMap({});
    } else {
      setCurrentPage(page);
    }
  };

  // NOTE:
  // As of writing this, Airtable doesn't provide a way to know the total records available in the get records api.
  // Current computation is if we have an offset in the api response, there are more records. If not, we've reached the end.
  // The limitation with this approach is we can't determine the total number of records, we can only increment the total by pageSize number of
  // records to get the next page.

  useEffect(() => {
    if (records?.length > 0) {
      // If we have an offset, there are more records
      if (offset) {
        setTotalRecords((prev) => Math.max(prev, currentPage * pageSize + 1));

        // Required for determining correct offset for all pages
        setOffsetMap((prev) => ({
          ...prev,
          [currentPage + 1]: offset,
        }));
      } else {
        // No offset means we've reached the end
        setTotalRecords(currentPage * pageSize);
      }
    } else if (records?.length === 0 && currentPage === 1) {
      // No records at all
      setTotalRecords(0);
    }
  }, [records, offset, currentPage, pageSize]);

  // Reset pagination when filters or table selection changes
  useEffect(() => {
    setCurrentPage(1);
    setTotalRecords(0);
    setOffsetMap({});
  }, [selectedBase, selectedTable, filterParams]);

  useEffect(() => {
    if (isUserRoleJrAg) {
      setAppliedFilters({
        UserId: userEmail,
      });
    }
  }, [isUserRoleJrAg]);

  // Set default base and table
  useEffect(() => {
    if (userBasesMenuOptions?.length) {
      setSelectedBase(userBasesMenuOptions[0]?.value as string);
    }
  }, [userBasesMenuOptions]);

  useEffect(() => {
    if (baseTableMenuOptions?.length) {
      setSelectedTable(baseTableMenuOptions[0]?.value as string);
    }
  }, [baseTableMenuOptions]);

  return (
    <PageHeaderLayout
      pageConfig={PAGE_CONFIG}
      renderFilters={() => (
        <RBCountryFilter
          selectedCountry={selectedCountry as string}
          onChange={setSelectedCountry}
        />
      )}
    >
      <Flex vertical gap={25} className="mt-n4">
        <Flex wrap="wrap" gap={20} align="flex-end" justify="space-between">
          <FilterSection
            baseOptions={userBasesMenuOptions}
            tableOptions={baseTableMenuOptions}
            selectedBase={selectedBase}
            selectedTable={selectedTable}
            onBaseChange={setSelectedBase}
            onTableChange={setSelectedTable}
            isBaseLoading={isBaseLoading}
            isTableLoading={isTableLoading}
            tableFilters={tableFilters}
            onTableFilterChange={handleFilterChange}
          />

          <ActionButtons
            onNewLead={() => setIsNewLeadFormOpen(true)}
            onRefresh={refetchTableRecords}
            isRefreshing={isTableRecordsFetching}
          />
        </Flex>

        {tableRecordsError ? (
          <Typography.Text type="danger">
            Selected table has no records associated with your user details.
            Please contact your admin if you think this is a bug.
          </Typography.Text>
        ) : (
          <SalesTable
            fieldsToRender={fieldsToRender}
            fieldMeta={selectedTableFieldMeta}
            loading={isTableRecordsLoading}
            records={records}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: totalRecords,
              onChange: handlePaginationChange,
            }}
          />
        )}
      </Flex>

      <LeadFormDrawer
        isOpen={isNewLeadFormOpen}
        onClose={() => setIsNewLeadFormOpen(false)}
        formUrl={selectedTableConfig?.urls?.newLead || ''}
        jrAgRecordId={jrAgRecordId}
      />
    </PageHeaderLayout>
  );
};

export default SalesPage;
